import { SAFETY_POLICY_LABELS, SAFETY_POLICY_TYPES, type SafetyPolicyType } from "@unbox/api";
import { useMemo } from "react";
import {
  useSaveWorkspaceSafetyConfig,
  useWorkspaceDomainKnowledge,
  useWorkspaceRiskCatalog,
  useWorkspaceRisksList,
} from "@/api/workspace-risks";
import type { SafetyPolicyOption } from "@/components/safety-config/ModalRiskRow";
import type { RiskConfigPatch } from "@/components/safety-config/RiskConfigModal";
import { RiskConfigModal } from "@/components/safety-config/RiskConfigModal";
import { success } from "@/providers/ToastProvider";

type WorkspaceConfigureModalProps = {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// The workspace stores a safety_policy_type per risk, not a concrete policy id.
// The shared modal keys policy options by `id`, so each policy type is offered as
// an option whose id IS the type — `safetyPolicyId` then carries the type.
const POLICY_OPTIONS: SafetyPolicyOption[] = SAFETY_POLICY_TYPES.map((type) => ({
  id: type,
  label: SAFETY_POLICY_LABELS[type],
  type,
}));

// Custom risk explanations are merchant-only, so the workspace drops that field.
const toWorkspaceEntry = ({ customCreatorRiskExplanation: _ignored, safetyPolicyId, ...patch }: RiskConfigPatch) => ({
  ...patch,
  safetyPolicyType: (safetyPolicyId as SafetyPolicyType | null) ?? null,
});

export const WorkspaceConfigureModal = ({ workspaceId, open, onOpenChange }: WorkspaceConfigureModalProps) => {
  const { data: catalog } = useWorkspaceRiskCatalog(workspaceId);
  const { data: workspaceRisksList } = useWorkspaceRisksList(workspaceId);
  const { data: domainKnowledge } = useWorkspaceDomainKnowledge(workspaceId);
  const { mutate: saveConfig, isPending } = useSaveWorkspaceSafetyConfig(workspaceId);

  const savedRisks = useMemo(
    () => (workspaceRisksList ?? []).map((risk) => ({ ...risk, safetyPolicyId: risk.safetyPolicyType })),
    [workspaceRisksList],
  );

  return (
    <RiskConfigModal
      open={open}
      onOpenChange={onOpenChange}
      title="Configure default risks"
      catalog={catalog}
      savedRisks={savedRisks}
      policies={POLICY_OPTIONS}
      savedDomainKnowledge={domainKnowledge}
      isSaving={isPending}
      managedPlaceholdersEditable
      onSave={(draft) =>
        saveConfig(
          {
            enabled: draft.enabled.map(toWorkspaceEntry),
            updated: draft.updated.map(toWorkspaceEntry),
            disabled: draft.disabled,
            domainKnowledge: draft.domainKnowledge,
          },
          {
            onSuccess: () => {
              success("Default safety configuration saved");
              onOpenChange(false);
            },
          },
        )
      }
    />
  );
};
